import React from 'react'
import {
  CaretRightOutlined,
  CloseCircleOutlined,
  PauseCircleOutlined,
  PauseOutlined,
} from '@ant-design/icons'
import { Stack } from '@sureco/design-system'
import {
  Alert,
  Button,
  Collapse,
  Progress,
  Tag,
} from '@surecompanies/core_components'
import dayjs from 'dayjs'
import { isFutureDatePST } from 'helpers/datetime'
import { useUsRestrictedDataAccess } from 'enrollment-platform/rbac/useUsRestrictedDataAccess'
import ContentPane from './ContentPane'
import EnrollmentItem from '../EnrollmentItem'
import EnrollmentOutstanding from './EnrollmentOutstanding'
import EnrollmentPeriodDates from './EnrollmentPeriodDates'
import EditEnrollmentDatesButton from './EditEnrollmentDatesButton'
import ComingSoonPanel from './ComingSoonPanel'
import { useClearStatus } from '../hooks/useClearStatus'
import { EnrollmentConfiguration } from '../hooks/useEnrollmentConfiguration'
import {
  PAUSE_COLOR,
  RESUME_COLOR,
  solidButtonStyle,
} from './pauseResumeStyles'

type EnrollmentConfigurationCardProps = {
  id: string
  enrollment: EnrollmentConfiguration
  currentYear: number
  downloadUrl?: string
  isLoadingDownloadUrls: boolean
  isResuming: boolean
  isPausing: boolean
  onResumeClick: (enrollmentId: string | null) => void
  onPauseClick: (enrollmentId: string | null) => void
}

const pillStyle: React.CSSProperties = {
  borderRadius: 999,
  border: 'none',
  fontWeight: 600,
}

export default function EnrollmentConfigurationCard({
  id,
  enrollment,
  currentYear,
  downloadUrl,
  isLoadingDownloadUrls,
  isResuming,
  isPausing,
  onResumeClick,
  onPauseClick,
}: EnrollmentConfigurationCardProps) {
  const { hasUsRestrictedDataAccess } = useUsRestrictedDataAccess()
  const enrollmentId = enrollment.id?.toString()
  const benefitYear = dayjs.utc(enrollment.benefitEffectiveDate).year()
  const isCurrentOrFutureYear = benefitYear >= currentYear
  // Pause/Resume targets an enrollment-year by its `id`; only rows created by
  // the current config path have one (legacy years have no id, so aren't
  // actionable). A new pause is offered only for the current or next benefit
  // year, but a paused enrollment must ALWAYS be resumable, regardless of year.
  const hasId = !!enrollment.id
  const isCurrentOrNextYear =
    benefitYear === currentYear || benefitYear === currentYear + 1
  const canPause = hasId && isCurrentOrNextYear
  const canResume = hasId && !!enrollment.isPaused
  const showPauseResume = canPause || canResume
  // Start/End Date changes are only allowed while the Benefit Effective Date is
  // still in the future — once it's today or earlier the enrollment period has
  // begun and dates are locked; a paused enrollment can't edit dates either
  // (matches the EPS reschedule constraints).
  const isBenefitEffectiveDateFuture = isFutureDatePST(
    enrollment.benefitEffectiveDate
  )
  const canEditDates =
    hasId && isBenefitEffectiveDateFuture && !enrollment.isPaused

  const clearStatus = useClearStatus(
    id,
    enrollmentId,
    !!enrollment.isPaused
  ).data
  // Only surface clear status while the enrollment is actually paused. A disabled
  // useClearStatus query still returns its last cached value, so without this an
  // old Failed/Clearing run would linger on an enrollment that is now active.
  const isClearing = !!enrollment.isPaused && clearStatus?.status === 'Clearing'
  const isFailed = !!enrollment.isPaused && clearStatus?.status === 'Failed'
  const clearPercent =
    clearStatus && clearStatus.total > 0
      ? Math.round((clearStatus.completed / clearStatus.total) * 100)
      : 0

  const renderStatusBadge = () => {
    if (isFailed) {
      return (
        <Tag icon={<CloseCircleOutlined />} color="error" style={pillStyle}>
          Clear failed
        </Tag>
      )
    }
    if (canResume) {
      return (
        <Tag
          icon={<PauseCircleOutlined />}
          color={PAUSE_COLOR}
          style={pillStyle}
        >
          Paused
        </Tag>
      )
    }
    return undefined
  }

  return (
    <ContentPane
      title={`Enrollment ${benefitYear}`}
      titleBadge={renderStatusBadge()}
      header={
        canEditDates || showPauseResume ? (
          <Stack direction="row" space={10}>
            {canEditDates && (
              <EditEnrollmentDatesButton id={id} enrollment={enrollment} />
            )}
            {showPauseResume &&
              (enrollment.isPaused ? (
                <Button
                  icon={<CaretRightOutlined />}
                  onClick={() => onResumeClick(enrollmentId ?? null)}
                  loading={isResuming}
                  disabled={isClearing}
                  style={
                    isClearing ? undefined : solidButtonStyle(RESUME_COLOR)
                  }
                >
                  Resume Enrollment
                </Button>
              ) : (
                <Button
                  icon={<PauseOutlined />}
                  onClick={() => onPauseClick(enrollmentId ?? null)}
                  loading={isPausing}
                  style={solidButtonStyle(PAUSE_COLOR)}
                >
                  Pause Enrollment
                </Button>
              ))}
          </Stack>
        ) : undefined
      }
    >
      {isClearing && (
        <div style={{ marginBottom: 12 }}>
          <div>
            Clearing carts… {clearStatus?.completed ?? 0} /{' '}
            {clearStatus?.total ?? 0}
          </div>
          <Progress percent={clearPercent} size="small" status="active" />
        </div>
      )}
      {isFailed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Cart clear failed"
          description={`Clearing carts failed and this enrollment remains paused. Contact engineering${
            clearStatus?.runId ? ` with run ID ${clearStatus.runId}` : ''
          }. To retry, resume again with “Also clear all carts” checked.`}
        />
      )}
      <EnrollmentItem
        config={enrollment}
        customerId={id}
        downloadUrl={downloadUrl}
        isLoadingDownloadUrls={isLoadingDownloadUrls}
      />

      {isCurrentOrFutureYear && (
        <Collapse key={`collapse-${enrollmentId || '1'}`}>
          {hasUsRestrictedDataAccess && (
            <Collapse.Panel
              key={`outstanding-${enrollmentId || '1'}`}
              header="Outstanding Enrollments"
            >
              <EnrollmentOutstanding id={id} enrollment={enrollment} />
            </Collapse.Panel>
          )}
          {canEditDates && (
            <Collapse.Panel
              key={`period-dates-${enrollmentId || '1'}`}
              header="Enrollment Period Dates"
            >
              <EnrollmentPeriodDates id={id} enrollment={enrollment} />
            </Collapse.Panel>
          )}
          <Collapse.Panel
            key={`activity-${enrollmentId || '1'}`}
            header="Activity"
          >
            <ComingSoonPanel message="An activity log for this enrollment is coming soon." />
          </Collapse.Panel>
        </Collapse>
      )}
    </ContentPane>
  )
}
